import { mappingValuesToLocaleTags } from "@/lib/config/localeTagHandler"

export type StrictMode = "Relaxed" | "Strict" | "Strict-404"

/**
 * Resolve the effective strict mode from a language config entry.
 * Reads the new `strictMode` enum field.
 */
export function resolveStrictMode(langConfig: any): StrictMode {
  const mode = langConfig?.strictMode
  if (mode === "Relaxed" || mode === "Strict" || mode === "Strict-404")
    return mode
  return "Relaxed"
}

/**
 * Whether content-level locale filtering should be applied.
 * True for both "Strict" and "Strict-404".
 */
export function isStrictFiltering(mode: StrictMode): boolean {
  return mode === "Strict" || mode === "Strict-404"
}

/**
 * Whether a page should 404 because it lacks the requested locale tag.
 * Only applies in "Strict-404" mode for non-English locales.
 */
export function shouldReturn404ForLocale(
  mode: StrictMode,
  contentfulLocale: string,
  tags?: { id: string }[],
): boolean {
  if (mode !== "Strict-404") return false
  if (contentfulLocale === "en-US") return false

  const expectedTag = mappingValuesToLocaleTags[contentfulLocale]
  if (!expectedTag) return false

  const tagIds = tags?.map(t => t.id) || []
  return !tagIds.includes(expectedTag)
}

// Entry types whose locale tags we query and can therefore check. Anything
// else (external links, product pages, ...) is always treated as translated.
export const STRICT_FILTER_TYPES = [
  "PageBase",
  "PageGeneral",
  "PageLongText",
  "RCLandingPage",
]

/**
 * Build a predicate telling whether a nav `pageLink` counts as translated.
 * Only enforced when strict filtering is on and the locale has a tag.
 */
export function createIsPageTranslated(
  contentfulLocale: string,
  isStrict: boolean,
): (pageLink: any) => boolean {
  const localeTag = mappingValuesToLocaleTags[contentfulLocale] || null
  return (pageLink: any) => {
    if (!pageLink || !isStrict || !localeTag) return true
    if (!STRICT_FILTER_TYPES.includes(pageLink.__typename)) return true
    const tags = pageLink.contentfulMetadata?.tags?.map((t: any) => t.id) || []
    return tags.includes(localeTag)
  }
}
